import { useTranslation } from 'react-i18next';

import { ScoreComparisonBar } from '~/components/common/score-comparison-bar';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RD3CItem {
  /** Optional stable dimension key (e.g. `relevance`) — keys the row's `data-testid`; falls back to the row index. */
  key?: string;
  /** Display percentage string (e.g. "25%"). */
  percentage: string;
  /** Dimension label. Pre-translated by the caller. */
  title: string;
  /** Optional explanatory text. Pre-translated by the caller. */
  description?: string;
  /** Before-enrichment score (0–10). */
  beforeScore: number;
  /** After-enrichment score (0–10). */
  afterScore: number;
}

interface RD3CProps {
  /** Section heading. Pre-translated by the caller. */
  title: string;
  /** Section sub-heading. Pre-translated by the caller. */
  subtitle: string;
  /** Ordered list of scoring dimensions to display. */
  items: RD3CItem[];
  /** `data-testid` root; derives `-title`, `-subtitle`, `-empty`, `-row-{key|index}` (+ `-percentage`, `-label`, `-description`, `-score`). */
  testId?: string;
}

/** Kebab-cases a dimension key for a `data-testid`. */
function toTestIdSlug(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Solid blue pill showing a dimension's weighting percentage.
 * Fixed at 35×24px with white semibold text per spec.
 */
function PercentageChip({ percentage, testId }: { percentage: string; testId: string }) {
  return (
    <div
      className="bg-primary flex h-6 w-[35px] shrink-0 items-center justify-center rounded-md px-2 py-0.5"
      data-testid={testId}
    >
      <span className="text-primary-foreground text-xs font-semibold">{percentage}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders a titled list of scoring dimensions (e.g. 3Cs, R & D), each showing
 * a percentage weight, label, description, and a before/after ScoreComparisonBar.
 * Rows are separated by a border and share a `bg-selected-row` background.
 */
export function RD3C({ title, subtitle, items, testId = 'rd3c' }: RD3CProps) {
  const { t } = useTranslation('common');

  return (
    <div className="flex flex-col" data-testid={testId}>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-1">
        <h3 className="text-foreground text-md font-medium" data-testid={`${testId}-title`}>
          {title}
        </h3>
        <p className="text-muted-foreground text-sm font-light" data-testid={`${testId}-subtitle`}>
          {subtitle}
        </p>
      </div>

      {/* Row list — overflow-hidden so the container clips row bg to rounded corners */}
      <div className="overflow-hidden rounded-sm">
        {items.length === 0 && (
          <p
            className="bg-surface-blue text-muted-foreground py-8 text-center text-sm"
            data-testid={`${testId}-empty`}
          >
            {t('rd3c.empty')}
          </p>
        )}
        {items.map((item, index) => {
          const rowTestId = `${testId}-row-${item.key ? toTestIdSlug(item.key) : String(index)}`;
          return (
            <div key={index} data-testid={rowTestId}>
              {index > 0 && <div className="border-border border-b" />}

              <div className="bg-surface-blue flex items-center gap-6 px-4 py-4">
                {/* Left: chip + label + description */}
                <div className="flex flex-1 flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <PercentageChip
                      percentage={item.percentage}
                      testId={`${rowTestId}-percentage`}
                    />
                    <span
                      className="text-foreground text-sm font-medium"
                      data-testid={`${rowTestId}-label`}
                    >
                      {item.title}
                    </span>
                  </div>

                  {item.description && (
                    <p
                      className="text-muted-foreground text-xs font-light"
                      data-testid={`${rowTestId}-description`}
                    >
                      {item.description}
                    </p>
                  )}
                </div>

                {/* Right: before/after score bars — fixed width keeps bars proportional */}
                <div className="w-full max-w-[50%] shrink-0">
                  <ScoreComparisonBar
                    beforeScore={item.beforeScore}
                    afterScore={item.afterScore}
                    testId={`${rowTestId}-score`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
